import axios from "axios";
import config from "./config";
import { cachedPost } from "./apiCache";

/**
 * Native learning adapter.
 *
 * The course/lesson data lives in the production funnel model (funnel_pages +
 * lessons), served by api/courselist / api/lessonlist / api/lesson-details /
 * api/markecomplete (JWT-gated). These helpers call those endpoints and reshape
 * the legacy response into the exact shape CourseViews already renders, so the
 * UI is unchanged. Tokens are just the numeric ids stringified:
 *   course_token = funnel_page_id, lesson_token = lesson_id.
 */
/**
 * Legacy modules can carry a stale placeholder in image_url (the old admin
 * wrote "emptyimage.jpeg" rather than leaving it empty). Treat those as no
 * image, the same rule the admin's own module popup applies, so a module the
 * creator never gave a cover doesn't render a grey rectangle.
 */
const MODULE_PLACEHOLDER_IMAGE = /emptyimage|placeholder|no[-_]?image/i;
const cleanModuleImage = (url) => {
  const u = String(url || "").trim();
  return !u || MODULE_PLACEHOLDER_IMAGE.test(u) ? "" : u;
};

/** Placeholder the API injects when a course has no cover of its own. */
const LEGACY_DEFAULT_COURSE_IMAGE =
  "https://membershipdata.flexifunnels.com/images/course_default.png";

function auth(jwt) {
  return { headers: { Authorization: `Bearer ${jwt}` } };
}

/** api/courselist -> { error, courses:[...], banner_img, page_title } */
export async function fetchCourses({ funnelId, memberId, jwt, page, perPage }) {
  // Coalesce/cache: the course list mounts once per view but StrictMode (dev)
  // and quick re-renders can fire it twice.
  const res = await cachedPost(
    `${config.API_URL}/courselist`,
    {
      funnel_id: funnelId,
      member_id: memberId,
      // Paging is opt-in on both sides. Omitted, the API answers with the whole
      // list and no paging keys — the shape it always had — so this still works
      // against an API that predates it.
      ...(page && perPage ? { page, per_page: perPage } : {}),
    },
    { ttl: 15000, auth: auth(jwt) }
  );
  const list = res.data?.courseData || [];
  const courses = list
    .filter((c) => c && c.funnel_page_id)
    .map((c) => {
      // The course grid reads `total_lessons` (getLessonCount from the API) and
      // `completedCount` (marked-complete lessons for this member). Clamp: the
      // API counts through `lessons`, but a stale count must never render "3/2".
      const total = Number(c.lessonCount || 0);
      const done = Math.min(Number(c.completedCount || 0), total);
      // Gamification gate, resolved by the API (member_level vs the course's
      // unlock_level). Only 'level' courses can come back level_unlocked:false.
      // Older API builds omit the flag — treat a missing flag as unlocked so the
      // catalog never locks itself against a bridge that can't answer.
      const levelLocked = c.level_unlocked === undefined ? false : !c.level_unlocked;
      // Course Access & Visibility time gates, resolved by the API:
      //   window_ended -> the drip window after enrolling has closed
      //   not_released -> a scheduled start date hasn't arrived yet
      // Older API builds omit these, so a missing flag means "open" and the
      // catalogue never locks itself against a bridge that can't answer.
      const accessLocked = !!c.access_locked;
      const lockReason = accessLocked ? String(c.access_lock_reason || "") : "";
      return {
        course_token: String(c.funnel_page_id),
        name: c.name || c.title || "",
        title: c.title || c.name || "",
        description: c.short_description || "",
        short_description: c.short_description || "",
        // The API substitutes a legacy placeholder PNG when the creator set no
        // cover (FunnelPage::getCourse). Treat that as "no image" so the card
        // falls back to the same default cover the admin shows. Not fixed in the
        // API because getCourse also feeds the legacy app, which still wants it.
        image:
          c.funnel_page_image && c.funnel_page_image !== LEGACY_DEFAULT_COURSE_IMAGE
            ? c.funnel_page_image
            : "",
        total_lessons: total,
        // Modules (`courses` rows) in this course. Older API builds don't send it,
        // and 0 simply hides the chip on the card rather than rendering "0 Modules".
        total_modules: Number(c.moduleCount || 0),
        completed_lessons: done,
        progress_pct: total > 0 ? Math.round((done / total) * 100) : 0,
        completed: total > 0 && done >= total,
        // Rating set in the admin (course_access_data.rating), surfaced by the API.
        star_rating: Number(c.star_rating || 0),
        // Assigned instructors (course_access_data.instructor_ids -> membership_instructors).
        // instructors[] is the list; the flat pair stays as the first, for
        // callers that still read a single name.
        instructors: Array.isArray(c.instructors) ? c.instructors : [],
        instructor_name: c.instructor_name || "",
        instructor_image: c.instructor_image || "",
        locked: !!c.access_expired || levelLocked || accessLocked,
        // Why it is locked, so the card can explain rather than just grey out.
        // "" when open. Bundle expiry keeps its existing separate fields.
        lock_reason: lockReason,
        release_at: c.release_at || null,
        // Countdown wording from the API ("You can access this course in 8 HOURS").
        release_message: c.release_message || "",
        access_expires_at: c.access_expires_at || null,
        // Creator's upgrade prompt (course_access_data), shown when the drip
        // window has ended.
        upgrade_text: c.upgrade_text || "",
        upgrade_button_text: c.upgrade_button_text || "",
        upgrade_button_link: c.upgrade_button_link || "",
        // True only for a level course the member hasn't reached — the card locks
        // and the click is blocked (a paid course they haven't bought instead gets
        // sent to its sales page, see openCourse).
        level_locked: levelLocked,
        member_level: Number(c.member_level || 0),
        member_points: Number(c.member_points || 0),
        // Card access badge: 2 = Paid (show price), 3 = Locked (expired / level-gated),
        // 1 = Free. Pricing type comes from course_access_data via the bridge.
        access_type:
          c.course_access_type === "paid"
            ? 2
            : c.access_expired || accessLocked || c.course_access_type === "level"
              ? 3
              : 1,
        price: Number(c.course_price || 0),
        currency: c.course_currency || "",
        // Gamification level a level-gated course unlocks at (0 = not level-gated).
        // Drives the "Level N" badge on the course card. The number is only the
        // creator's list position, so the badge prefers the level's own NAME and
        // the message its point cost.
        unlock_level: Number(c.course_unlock_level || 0),
        unlock_level_name: c.course_unlock_level_name || "",
        unlock_points: Number(c.course_unlock_points || 0),
        intro_video: c.inro_video || null,
        enroll_date: c.enroll_date || null,
        // Purchase gate (bridge): whether this member holds the course, and the
        // published sales page to send them to when they don't. Set for an unbought
        // PAID course and for a FREE course the creator sells through a product —
        // that product's $0 order is what enrols them. An ordinary free course and
        // a level course come back purchased:true with no sales_url.
        purchased: c.purchased === undefined ? true : !!c.purchased,
        sales_url: c.sales_url || "",
        // Every product that sells this course, when more than one does — a course
        // can sit in the bundles of a Starter, a Pro and an All Access, each with
        // its own sales page and price. `sales_url` can only name the first, so the
        // card used to send every buyer to that one offer whatever they wanted.
        // With two or more entries the click opens a chooser instead (see
        // openCourse in CourseViews); one entry behaves exactly as before.
        //
        // An older API build sends no `offers` at all — an empty list, not a
        // missing chooser, so the single-link path stays in charge.
        offers: Array.isArray(c.offers)
          ? c.offers
              .filter((o) => o && (o.url || o.sales_url))
              .map((o) => ({
                product_id: Number(o.product_id || 0),
                name: o.name || "This offer",
                url: o.url || o.sales_url || "",
                price: Number(o.price || 0),
                currency: o.currency || "",
                pricing_type: o.pricing_type || "",
                frequency: o.frequency || "",
                // The creator's pick (offer_mode 'recommend') — listed first and
                // badged by CourseOfferModal.
                recommended: !!o.recommended,
              }))
          : [],
        // Course creation date (funnel_pages.created_at) — surfaced on the card.
        created_at: c.created_at || null,
        sort_order: Number(c.sort_order || 0),
      };
    });
  return {
    error: 0,
    courses,
    banner_img: res.data?.banner_img || "",
    page_title: res.data?.page_title || "Courses",
    // Membership Grade toggle (admin: Settings → Features). Off hides the
    // Free/Paid/level badge on course cards.
    //
    // Missing means ON, not off: an older API build that predates the flag
    // omits it entirely, and treating that as "hidden" would silently strip the
    // badge from every membership the moment the frontend shipped ahead of the
    // API. Only an explicit 0 hides it.
    grade_mode: res.data?.grade_mode === undefined ? 1 : Number(res.data.grade_mode),
    // Course Rating toggle (admin: Settings → Features). Off hides the star
    // rating on every surface a member meets a course. Missing means ON, for the
    // same reason grade_mode does: an older API build omits the key entirely,
    // and treating that as "hidden" would strip the rating from every membership
    // the moment the frontend shipped ahead of the API.
    rating_mode: res.data?.rating_mode === undefined ? 1 : Number(res.data.rating_mode),
    // Paging metadata. An API that does not page reports neither, and the caller
    // falls back to counting what it received.
    total: res.data?.total,
    totalPages: res.data?.total_pages,
  };
}

function mapLessonType(t) {
  if (t === "video") return 3;
  if (t === "audio") return 2;
  return 1; // text / content
}

// Raw provider embed HTML — rendered as-is (dangerouslySetInnerHTML), matching
// the reference my-learning player. Two sources, both already HTML <iframe>/<div>:
//   - generic_video + generic_video_type === "embed" : course-author custom embed
//   - embed : DRM player snippet (VdoCipher `flexi` / Gumlet `flexi_gumlet`),
//             server-signed (OTP) and only present when the member has drm_mode on.
// These can't be reduced to a plain src (DRM watermark/script, multi-element), so
// the player renders the snippet verbatim.
function resolveEmbedHtml(det) {
  if (det.generic_video && String(det.generic_video_type) === "embed") return det.generic_video;
  if (det.embed) return det.embed;
  return null;
}

// FlexiFunnels stores hosted videos as (video_type, video_id) — not a single URL
// — so build an embeddable player URL. Embed-HTML providers are handled by
// resolveEmbedHtml above and are NOT returned here.
// videoData is stored (often double-)JSON-encoded; decode up to twice to an object.
function parseVideoData(raw) {
  let v = raw;
  for (let i = 0; i < 2 && typeof v === "string" && v; i += 1) {
    try { v = JSON.parse(v); } catch { return null; }
  }
  return v && typeof v === "object" ? v : null;
}

// Gumlet asset id: prefer the top-level video_id, else the asset_id buried in
// videoData. Many gumlet lessons store NO video_id (it lives only in videoData),
// so without this fallback those lessons resolve to no source and show no video.
// A Gumlet asset id is 24 hex characters; a VdoCipher id is 32. That matters for
// `flexi` (see resolveMediaUrl), where video_id may be either.
function gumletAssetId(det) {
  if (det.video_id) return det.video_id;
  const vd = parseVideoData(det.videoData);
  return vd?.asset_id || vd?.video_id || null;
}

/** True for a Gumlet asset id — 24 hex chars, as every Gumlet asset uses. */
function looksLikeGumletId(id) {
  return typeof id === "string" && /^[a-f0-9]{24}$/i.test(id);
}

/** Standard (non-DRM) Gumlet player URL for an asset id. */
function gumletEmbedUrl(asset) {
  // settings=true keeps Gumlet's gear menu, which is where a viewer picks
  // QUALITY and PLAYBACK SPEED. It was false, so those lessons had neither —
  // the menu is the only place Gumlet offers them.
  return `https://play.gumlet.io/embed/${asset}?autoplay=false&controls=true&pip=false&background=false&settings=true`;
}

/**
 * A module's intro video (courses.video_id / video_type / videoData), normalised
 * into something the player can render — or null when there is nothing playable.
 *
 * The module row is NOT a lesson payload: api/lessonlist hands the three raw
 * columns straight through, with none of the `generic_video` / `embed` fields
 * api/lesson-details composes. So the generic case is unpacked here from the blob
 * the admin's picker wrote (customEmbed 1 = a pasted snippet, 2 = a plain URL),
 * and everything else is handed to the SAME resolvers a lesson uses — youtube,
 * vimeo, gumlet, flexi_gumlet and Gumlet-migrated flexi all resolve client-side
 * from (video_type, video_id, videoData) with no signing.
 *
 * Returning null for an unresolvable video is deliberate: a module whose video is
 * still processing, or a DRM one that can only play from a signed OTP, shows no
 * intro affordance at all rather than a play button that opens an empty frame.
 */
export function buildIntroVideo(m) {
  const videoType = String(m?.video_type || "").toLowerCase();
  const data = parseVideoData(m?.videoData) || {};
  if (!videoType && !m?.video_id && !Object.keys(data).length) return null;

  // Generic / HTML5: the picker stores the snippet or the URL in the blob, and
  // resolveEmbedHtml / resolveMediaUrl read them off the lesson-shaped keys.
  const isGeneric = videoType === "generic_video";
  const det = {
    video_type: videoType,
    video_id: m?.video_id || "",
    videoData: m?.videoData ?? null,
    generic_video: isGeneric
      ? (Number(data.customEmbed) === 1 ? data.embed_code || "" : data.video_url || "")
      : "",
    generic_video_type: isGeneric && Number(data.customEmbed) === 1 ? "embed" : "",
  };

  const embed_html = resolveEmbedHtml(det);
  const url = embed_html ? null : resolveMediaUrl(det);
  if (!embed_html && !url) return null;

  return {
    url,
    embed_html,
    video_type: videoType,
    video_id: det.video_id,
    // Poster: what the creator's pick came with, else the module's own cover.
    thumbnail: data.video_thumbnail_url || cleanModuleImage(m?.image_url) || "",
    title: String(data.title || "").trim(),
  };
}

function resolveMediaUrl(det) {
  // generic_video as a pasted URL (the "embed" variant is handled by resolveEmbedHtml).
  if (det.generic_video && String(det.generic_video_type) !== "embed") return det.generic_video;
  if (det.video) return det.video;
  if (det.media_url) return det.media_url;
  const type = String(det.video_type || "").toLowerCase();
  // Gumlet (plain) and non-DRM flexi_gumlet: the Gumlet asset id is video_id when
  // present, else asset_id inside videoData. (DRM variants come through
  // resolveEmbedHtml.) Player params mirror the reference.
  if (type === "gumlet" || type === "flexi_gumlet") {
    const asset = gumletAssetId(det);
    return asset ? gumletEmbedUrl(asset) : null;
  }
  /*
   * `flexi` — FlexiFunnels' own hosted video, and the one provider the player
   * did not know about at all: it fell through to the `return null` below, so a
   * flexi lesson rendered with NO player and NO message, as though it had never
   * had a video. The reference learner app (Course_Membership my-learning.js)
   * has an explicit branch for it, feeding lessionDetailes.embed to VdoPlayer.
   *
   * A flexi lesson plays from ONE of two sources:
   *
   *   embed      the server-signed VdoCipher/Gumlet snippet. VdoCipher cannot be
   *              played from a bare id — every play needs a fresh OTP — so this
   *              is the only route for a video still on VdoCipher, and it is
   *              resolveEmbedHtml's job, not this function's.
   *   Gumlet id  flexi videos migrated to Gumlet. video_id is then a 24-hex
   *              Gumlet asset id (VdoCipher's are 32), or the asset id sits in
   *              videoData. Either way it plays through the standard Gumlet
   *              player with no signing at all.
   *
   * Only the second is reachable from here, and only when the id really does
   * look like Gumlet's — feeding a 32-char VdoCipher id to play.gumlet.io would
   * swap "no video" for a broken player, which is worse.
   */
  if (type === "flexi") {
    const asset = gumletAssetId(det);
    return looksLikeGumletId(asset) ? gumletEmbedUrl(asset) : null;
  }
  const id = det.video_id;
  if (!id) return null;
  if (type === "youtube") return `https://www.youtube.com/embed/${id}`;
  if (type === "vimeo") return `https://player.vimeo.com/video/${id}`;
  // Anything left with no playable URL — a `flexi` lesson still on VdoCipher
  // whose only source is the signed embed, or a video that has not finished
  // processing. Returning null lets the player show the "currently unavailable"
  // card (the reference's "This video is currently unavailable.") instead of a
  // broken iframe or, as before, nothing at all.
  return null;
}

// Legacy FlexiFunnels lessons store the whole authored page in `html`. For videos
// imported from YouTube/Vimeo/etc. that bundle is `<h2>{title}</h2>` + description +
// `<div class="lesson-video"><iframe>…</iframe></div>`. The new player already renders
// the lesson title (as its own <h2>) and the video (via media_url / embed_html), so
// those pieces inside the html show up as a duplicated title and a second video below
// the description. Strip them so the body shows only the description text.
/**
 * True when `html` carries no visible content.
 *
 * api/lesson-details substitutes `html = '&nbsp;'` for a Video lesson that has
 * videoData + a video_id but an empty `html` column (LessonController: "if
 * description is empty and video data is not empty, show markcomplete button").
 * That placeholder is NOT empty, so `det.html || det.description` picked it and
 * the lesson's real `description` was never read — the About Lesson tab rendered
 * one blank line. Lessons authored in the newer admin write `description` and
 * leave `html` empty, so every video lesson created there lost its description
 * while legacy lessons (which fill `html`) looked fine.
 *
 * A media-only body still counts as content: an image or embed with no text is
 * a real description.
 */
function isBlankHtml(html) {
  const raw = String(html ?? "");
  if (!raw) return true;
  if (/<(img|iframe|video|audio|table)\b/i.test(raw)) return false;

  return !raw
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
}

function cleanLessonHtml(html, { title, hasVideo } = {}) {
  if (!html) return "";
  let out = html;

  // Drop a leading heading that just repeats the lesson title.
  if (title) {
    const norm = (s) => s.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim().toLowerCase();
    out = out.replace(/^\s*<(h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/i, (m, _tag, inner) =>
      norm(inner) === norm(title) ? "" : m
    );
  }

  // Drop the embedded <div class="lesson-video">…</div> player — the same video is
  // already shown by the dedicated player above the description.
  if (hasVideo) {
    out = out.replace(/<div\s+class="lesson-video"[^>]*>[\s\S]*?<\/div>/gi, "");
  }

  return out.trim();
}

/**
 * api/lessonlist WITHOUT the per-lesson api/lesson-details fan-out.
 *
 * The course search only matches on lesson names, so hydrating embed_html /
 * media_url / description for every unlocked lesson (one request each) would
 * cost N extra calls per course for data no result row renders. Flattens the
 * modules away — search doesn't group by module.
 *
 * Interim: there is no server-side search endpoint, so the index is built by
 * calling this once per course. Replace with a single search endpoint when the
 * catalog outgrows a few dozen courses.
 */
export async function fetchCourseLessonsLite({ funnelId, memberId, jwt, courseToken }) {
  const funnelPageId = parseInt(courseToken, 10);
  const res = await axios.post(
    `${config.API_URL}/lessonlist`,
    { funnel_id: funnelId, member_id: memberId, funnel_page_id: funnelPageId },
    auth(jwt)
  );
  const courseObj = res.data?.course || {};
  const moduleVals = Object.values(courseObj)
    .filter((m) => m && typeof m === "object" && m.course_id && m.lessons)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return moduleVals.flatMap((m) => {
    // Same module-drip rule as fetchCourseDetail — both paths must agree, or the
    // sidebar and the player disagree about what is open.
    const moduleLocked = Number(m.show) === 0;
    return Object.values(m.lessons)
      .filter((l) => l && typeof l === "object" && l.lesson_id)
      .map((l) => ({
        lesson_token: String(l.lesson_id),
        title: l.lesson_name || "",
        completed: Number(l.markcomplete_status) === 1,
        locked: moduleLocked || Number(l.show) === 0,
        lock_message: moduleLocked ? m.restrict_text || "" : l.restrict_text || "",
        lesson_type: mapLessonType(l.lesson_type),
        // The video PROVIDER, distinct from lesson_type above.
        //
        //   lesson_type  the KIND of lesson — api/lessonlist sends the string
        //                "video" or "text", which mapLessonType turns into 3/1
        //   video_type   the provider — "flexi_gumlet", "gumlet", "youtube",
        //                "vimeo", "flexi", "generic_video"
        //
        // The two were easy to confuse because the list used to send only
        // lesson_type, so a flexi_gumlet lesson looked like plain "video" here
        // and the provider was unknowable until api/lesson-details had been
        // fetched for that single lesson. Kept raw (not mapped) because
        // resolveMediaUrl / resolveEmbedHtml branch on these exact strings.
        video_type: l.video_type || "",
        video_id: l.video_id || "",
        videoData: l.videoData ?? null,
        module_name: m.course_name || "",
        module_locked: moduleLocked,
        course_token: String(courseToken),
      }));
  });
}

/**
 * `release_content` (course-level) arrives as a JSON *string* — either the raw
 * funnel_pages.course_access_data column, or one the API composed for a
 * not-yet-released course. Decode defensively; a course with no upgrade copy
 * configured still has to lock, just with the generic message.
 */
function parseRestrictContent(raw) {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

/** Every empty content field a lock result must carry, so merging it over a
 *  previously-hydrated lesson clears that lesson's media/description. */
const NO_CONTENT = {
  embed_html: null,
  media_url: null,
  detailed_description: "",
  goal: null,
  video_type: "",
  video_status: null,
  body_has_video: false,
};

/**
 * api/lesson-details for ONE lesson.
 *
 * The endpoint answers in one of three shapes, and only the first carries
 * content (LessonController@lessonDetails):
 *
 *  1. open        -> { lesson: {...}, success }
 *  2. lesson lock -> flat { show: 0, restrict_text, buttontext?, buttonink?,
 *                           access_expired?, expired_at? } — the member is not
 *                    entitled to this lesson (lessons.upgrade_data supplies the
 *                    "Upgrade" copy + button), or a drip prerequisite is unmet.
 *  3. course lock -> flat { restrictCourse: true, release_content } — the WHOLE
 *                    course is shut: the access window since purchase has run
 *                    out (restrict_day_mode) or its start date hasn't arrived
 *                    (release_date_mode). release_content is
 *                    {upgradetext, buttontext, buttonlink}.
 *
 * Shapes 2 and 3 used to fall through the `d.data?.lesson` guard and return
 * null, so the player hydrated an empty lesson: no padlock, no message, no
 * upgrade button. Both are now returned as lock results the player renders.
 *
 * Returns null only when the response has no usable payload at all.
 */
export async function fetchLessonDetail({ funnelId, memberId, jwt, courseToken, lessonToken, title }) {
  const d = await axios.post(
    `${config.API_URL}/lesson-details`,
    {
      funnel_id: funnelId,
      member_id: memberId,
      funnel_page_id: parseInt(courseToken, 10),
      lesson_id: parseInt(lessonToken, 10),
    },
    auth(jwt)
  );
  const data = d.data || {};

  // (3) Course-level restriction — nothing in this course is viewable, so the
  // player shows the creator's upgrade screen instead of the lesson.
  if (data.restrictCourse === true || data.restrictCourse === 1 || data.restrictCourse === "true") {
    const rc = parseRestrictContent(data.release_content);
    return {
      ...NO_CONTENT,
      course_locked: true,
      locked: true,
      lock_reason: "course_restricted",
      // Legacy writes `upgradetext`; some rows carry `upgradeText`. Same
      // fallback line the reference member app used.
      lock_message: rc.upgradetext || rc.upgradeText || "Currently the course access has been restricted",
      upgrade_button: rc.buttontext || "",
      // The composed not-yet-released payload uses buttonlink; course_access_data
      // has been seen with the legacy `buttonink` typo too.
      upgrade_link: rc.buttonlink || rc.buttonink || "",
    };
  }

  /*
   * `lesson` is normally a LIST, but the API has shipped it as an OBJECT keyed
   * "0" whenever a scalar got written to the outer array instead of the row —
   * LessonController's DRM flexi_gumlet branch did exactly that
   * ($lessondet['drm_mode'] = 1, missing its [$key]), and PHP's json_encode
   * then emits {"0":{…},"drm_mode":1} rather than [{…}].
   *
   * Unwrapping with Array.isArray alone kept the WRAPPER in that case, so
   * video_type / embed / html were all undefined and the lesson rendered with
   * no video and no description. The API side is fixed, but this stays because
   * the two shapes are indistinguishable to a client and any future stray
   * scalar would silently break playback again. The legacy learner app
   * (Course_Membership, my-learning.js) unwraps the same way, with
   * Object.values(resp.lesson)[0].
   *
   * Picks the first OBJECT value rather than blindly [0], so a scalar sitting
   * ahead of the row cannot be mistaken for the lesson.
   */
  let det = data.lesson;
  if (det && typeof det === "object") {
    det = Object.values(det).find(
      (v) => v && typeof v === "object" && !Array.isArray(v)
    );
  }

  // (2) Lesson-level restriction. `show: 0` is the entitlement/upgrade lock; a
  // bare restrict_text with no lesson is the drip-prerequisite lock.
  if (Number(data.show) === 0 || (!det && data.restrict_text)) {
    return {
      ...NO_CONTENT,
      locked: true,
      // The bridge now names the lock ('upgrade' for an upgrade-locked lesson or
      // its parent module). Prefer it: hydrate() merges this over the list's row,
      // so hardcoding "access" here downgraded a correct 'upgrade' to the generic
      // grey "Access Forbidden!" treatment and dropped the upsell styling.
      // 'access' stays the fallback for the older responses that send no reason.
      lock_reason: data.access_expired
        ? "access_expired"
        : data.lock_reason || "access",
      lock_message: data.restrict_text || "Access Forbidden!",
      upgrade_button: data.buttontext || "",
      upgrade_link: data.buttonlink || data.buttonink || "",
    };
  }

  if (!det || typeof det !== "object") return null;

  const embed_html = resolveEmbedHtml(det);
  const media_url = resolveMediaUrl(det);
  const detailed_description = cleanLessonHtml(
    isBlankHtml(det.html) ? (det.description || "") : det.html,
    { title, hasVideo: Boolean(embed_html || media_url) }
  );
  return {
    embed_html,
    media_url,
    /*
     * A legacy lesson can carry its player INSIDE the html body — the
     * `<div class="lesson-video"><iframe>…</iframe></div>` bundle the old admin
     * wrote — and cleanLessonHtml only strips that when a dedicated player is
     * already showing above it. Such a lesson has no embed_html and no
     * media_url and yet is not missing its video at all, so the "unavailable"
     * card must not accuse it. */
    body_has_video: /<iframe\b|<video\b/i.test(detailed_description),
    /*
     * The provider, carried through to the player so it can say something
     * useful when neither source resolved. A `flexi` / `flexi_gumlet` lesson
     * with no embed and no id is a PLATFORM video that could not be signed
     * (api/lesson-details only mints the signed embed for DRM members), not a
     * lesson someone forgot to add a video to — and the two want different
     * messages. See the "video unavailable" card in CourseViews.
     */
    video_type: String(det.video_type || "").toLowerCase(),
    // upload_videos.status, echoed by the API for the platform providers.
    // Absent means there is no ready video row behind this lesson — still
    // transcoding, or deleted.
    video_status: det.video_status ?? null,
    // Prefers `html`, but falls through to `description` when html is only the
    // API's '&nbsp;' placeholder (or otherwise blank) — see isBlankHtml.
    detailed_description,
    goal: det.goal || null,
  };
}

/**
 * api/lessonlist -> { error, course, course_name, modules:[{ name, lessons:[...] }] }.
 * `course` from lessonlist is an object keyed 0,1,2,… (+ a `firstlessons`
 * single lesson); each module's `lessons` is likewise an index-keyed object
 * with trailing aggregate scalars — so we keep only real objects with ids.
 *
 * Lessons come back as content-less stubs (`hydrated: false`). This used to
 * fan out an api/lesson-details call per unlocked lesson up front, but only the
 * lesson being viewed ever renders that content — see fetchLessonDetail, which
 * the player calls for the active lesson (and prefetches for the next one).
 */
/**
 * Approved reviews for a course's Reviews tab.
 *
 * Answers `{enabled:false, reviews:[]}` rather than an error when the creator has
 * the tab switched off, so the caller has one shape to read either way. Never
 * carries the reviewer's email — see the API's listForCourse.
 */
export async function fetchCourseReviews({ funnelId, courseToken }) {
  const funnelPageId = parseInt(courseToken, 10);
  if (!funnelId || !funnelPageId) return { enabled: false, reviews: [], count: 0, average: 0 };
  try {
    const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-course-reviews`, {
      funnel_id: funnelId,
      funnel_page_id: funnelPageId,
      base_url: window.location.host,
    });
    const d = res.data || {};
    return {
      enabled: !!d.enabled,
      reviews: Array.isArray(d.reviews) ? d.reviews : [],
      count: Number(d.count || 0),
      average: Number(d.average || 0),
    };
  } catch {
    // A tab that cannot load is a tab that should not be there.
    return { enabled: false, reviews: [], count: 0, average: 0 };
  }
}

export async function fetchCourseDetail({ funnelId, memberId, jwt, courseToken }) {
  const funnelPageId = parseInt(courseToken, 10);
  const res = await axios.post(
    `${config.API_URL}/lessonlist`,
    { funnel_id: funnelId, member_id: memberId, funnel_page_id: funnelPageId },
    auth(jwt)
  );

  const courseObj = res.data?.course || {};
  const moduleVals = Object.values(courseObj)
    .filter((m) => m && typeof m === "object" && m.course_id && m.lessons)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const modules = moduleVals.map((m) => {
    const lessons = Object.values(m.lessons)
      .filter((l) => l && typeof l === "object" && l.lesson_id)
      .map((l) => ({
        lesson_token: String(l.lesson_id),
        title: l.lesson_name || "",
        name: l.lesson_name || "",
        completed: Number(l.markcomplete_status) === 1,
        locked: Number(l.show) === 0,
        lesson_type: mapLessonType(l.lesson_type),
        // The video PROVIDER, distinct from lesson_type above.
        //
        //   lesson_type  the KIND of lesson — api/lessonlist sends the string
        //                "video" or "text", which mapLessonType turns into 3/1
        //   video_type   the provider — "flexi_gumlet", "gumlet", "youtube",
        //                "vimeo", "flexi", "generic_video"
        //
        // The two were easy to confuse because the list used to send only
        // lesson_type, so a flexi_gumlet lesson looked like plain "video" here
        // and the provider was unknowable until api/lesson-details had been
        // fetched for that single lesson. Kept raw (not mapped) because
        // resolveMediaUrl / resolveEmbedHtml branch on these exact strings.
        video_type: l.video_type || "",
        video_id: l.video_id || "",
        videoData: l.videoData ?? null,
        // Structured lock code from the bridge ('drip_prev' | 'access' | ''),
        // NOT the human message — the lesson list keys its drip styling and copy
        // off the code (lock_reason.startsWith("drip")). The message is kept
        // separately for the "why is this locked" text.
        lock_reason: l.lock_reason || "",
        lock_message: l.restrict_text || "",
        // Set only when the lock is a LAPSED entitlement (lock_reason
        // 'access_expired'), so the player can say when it ended rather than just
        // that it is shut.
        expired_at: l.expired_at || "",
        // Machine-readable drip unlock date (Y-m-d) for date-based drip, so the UI
        // can render "Unlocks on <date>" itself. Empty for prerequisite/access locks.
        unlock_on: l.unlock_on || "",
        // Level a level-gated lesson opens at (0 when the lock isn't a level one).
        unlock_level: Number(l.unlock_level || 0),
        // Per-lesson upgrade CTA (from lessons.upgrade_data): message is lock_message
        // above; these drive the "Upgrade" button. Bridge sends the link as buttonink
        // (legacy typo) and now also buttonlink — read either.
        upgrade_button: l.buttontext || "",
        upgrade_link: l.buttonlink || l.buttonink || "",
        // Droplet indicator for any drip lock (prerequisite or scheduled date).
        drip_type: String(l.lock_reason || "").startsWith("drip") ? 1 : 0,
        has_goal: !!l.has_goal,
        quiz_status: l.quiz_status || 0,
        media_url: null,
        detailed_description: "",
        // Content (media / html / goal) is loaded on demand by the player.
        hydrated: false,
      }));
    // Module-level drip (drip_feeds: a specific date, or N days from joining).
    // The API returns show/restrict_text PER MODULE; this used to keep only the
    // per-lesson flags, so a dripped module arrived looking wide open. Its lessons
    // inherit the lock so prefetch and auto-advance skip them too.
    const moduleLocked = Number(m.show) === 0;
    /*
     * WHY the module is locked. Three kinds now reach here, and they must stay
     * apart — the player styles them by this code alone:
     *
     *   upgrade -> courses.upgrade_data. Only a purchase opens it.
     *   access  -> the member's bundles do not grant this module. Nothing on the
     *              screen will open it; it is the module-level twin of the
     *              per-lesson "Access Forbidden!".
     *   (else)  -> drip / release date. Genuinely "come back later".
     *
     * This used to be a BINARY test — upgrade, else assume time-based — which was
     * safe only while drip and upgrade were the sole module locks. The API now
     * also sends lock_reason:"access" for an ungranted module, and folding that
     * into "drip_module" made CourseViews paint it amber with a clock and the
     * word SCHEDULED, telling a member to wait for something that waiting cannot
     * open. `access` is passed straight through: CourseViews already maps it to
     * "Access Forbidden!" and, because it does not start with "drip", renders the
     * chip in the community's own theme colour rather than the amber.
     */
    const moduleUpgradeLocked = moduleLocked && m.lock_reason === "upgrade";
    const moduleAccessLocked = moduleLocked && m.lock_reason === "access";
    const moduleLockReason = moduleUpgradeLocked
      ? "upgrade_module"
      : moduleAccessLocked
        ? "access"
        : moduleLocked
          ? "drip_module"
          : "";
    // The creator's CTA off the module's own upgrade lock. The lock screen
    // already knows how to render these (upgrade_button / upgrade_link); they
    // simply had no way to travel from a MODULE before.
    const moduleUpgradeButton = m.buttontext || "";
    const moduleUpgradeLink = m.buttonlink || m.buttonink || "";
    return {
      name: m.course_name || "",
      module_id: m.course_id,
      // What the creator wrote in the admin's module popup. Both are optional
      // and both used to stop at the admin — the player now shows them inside
      // the expanded module.
      description: String(m.description || "").trim(),
      image: cleanModuleImage(m.image_url),
      // The module's own intro video, if the creator set one — courses.video_id /
      // video_type / videoData, which api/lessonlist already sends per module.
      // Null when there is none, or when it cannot be played without a signed OTP.
      intro_video: buildIntroVideo(m),
      locked: moduleLocked,
      lock_reason: moduleLockReason,
      lock_message: m.restrict_text || "",
      upgrade_button: moduleUpgradeButton,
      upgrade_link: moduleUpgradeLink,
      // A locked module's lessons inherit both the lock AND its message, so the
      // player shows "THIS MODULE WILL UNLOCK IN 17 HOURS" instead of the generic
      // per-lesson "Access Forbidden!" the entitlement check produces.
      lessons: moduleLocked
        ? lessons.map((l) => ({
            ...l,
            locked: true,
            // Neither an upgrade-locked nor an ungranted module is "scheduled" —
            // nothing about waiting opens either — so they must not inherit the
            // drip reason, which is what paints the amber clock chip.
            lock_reason: moduleLockReason || "drip_module",
            lock_message: m.restrict_text || l.lock_message || "",
            // Carry the module's CTA down so the lock screen can offer it. Only for
            // an upgrade lock: a dripped module has nothing to buy, and a lesson's
            // own CTA (from lessons.upgrade_data) still wins where it has one.
            ...(moduleUpgradeLocked
              ? {
                  upgrade_button: l.upgrade_button || moduleUpgradeButton,
                  upgrade_link: l.upgrade_link || moduleUpgradeLink,
                }
              : {}),
          }))
        : lessons,
    };
  });

  // Course-level gamification gate from the API: a 'level' course stays shut
  // until member_level >= unlock_level. level_unlocked is authoritative — the
  // lessons are locked server-side too, this just lets the player say WHY.
  const unlock_level = Number(res.data?.unlock_level || 0);
  const unlock_level_name = res.data?.unlock_level_name || "";
  const unlock_points = Number(res.data?.unlock_points || 0);
  const member_level = Number(res.data?.member_level || 0);
  const level_locked = res.data?.level_unlocked === undefined ? false : !res.data.level_unlocked;

  // Otherwise access is per lesson (show=1/0, mapped to l.locked): treat the
  // course as viewable if any lesson is open, so accessible courses don't show a
  // spurious "This course is locked" banner.
  // Lessons inside a dripped module are marked locked above, so this now covers
  // module drip as well: a course whose every module is still dripping reads as
  // not-viewable instead of open-but-empty.
  const allLessons = modules.flatMap((m) => m.lessons);
  const can_view = !level_locked && allLessons.some((l) => !l.locked);

  /*
   * Header fields, straight from this endpoint (FunnelPage::courseHeader).
   *
   * The player used to read the cover, blurb, rating, instructors and the
   * Purchased badge off the CATALOGUE row instead, because api/lessonlist did
   * not carry them. The catalogue is served six at a time and its page resets
   * on a refresh, so a member who reloaded inside a course further down the
   * list had no row to read and the header lost all five for good — only the
   * name and the two counts, which come from here, survived.
   *
   * `instructors` is the capability probe: an array against an API that has
   * courseHeader, undefined against one that predates it. The player uses it to
   * decide whether it knows the access state at all, so an older bridge still
   * falls back to the catalogue row rather than asserting "Free".
   */
  const headerKnown = res.data?.instructors !== undefined;
  const cover = res.data?.funnel_page_image || res.data?.course_image || res.data?.banner_img || "";
  const instructors = Array.isArray(res.data?.instructors) ? res.data.instructors : [];

  return {
    error: 0,
    course: {
      course_token: courseToken,
      name: res.data?.course_name || "",
      // Same rule the catalogue applies: the legacy placeholder PNG means "no
      // cover", and the app shows its own default instead.
      image: cover && cover !== LEGACY_DEFAULT_COURSE_IMAGE ? cover : "",
      short_description: res.data?.short_description || "",
      star_rating: Number(res.data?.star_rating || 0),
      instructors,
      instructor_name: res.data?.instructor_name || "",
      instructor_image: res.data?.instructor_image || "",
      /*
       * Card access badge, mapped exactly as the catalogue maps it (see
       * fetchCourses): 2 = Paid, 3 = Locked, 1 = Free. The catalogue also reads
       * 3 off its time gates (access_expired / access_locked); this endpoint
       * does not resolve those, and it does not need to — the player discovers a
       * time-gated course from its first lesson-details call and swaps the whole
       * screen for the lock overlay. undefined on an older API, so the merge in
       * CourseViews falls through to the catalogue row.
       */
      access_type: headerKnown
        ? (res.data?.course_access_type === "paid"
            ? 2
            : res.data?.course_access_type === "level"
              ? 3
              : 1)
        : undefined,
      purchased: headerKnown ? !!res.data?.purchased : undefined,
      header_known: headerKnown,
      // The landing gates its progress UI on these; without them total=0 and it
      // falls back to the lock message even when the course is viewable.
      total_lessons: allLessons.length,
      completed_lessons: allLessons.filter((l) => l.completed).length,
      unlock_level,
    },
    course_name: res.data?.course_name || "",
    // Where an expired / unentitled member goes to buy this course. '' when the
    // course has no sales or checkout page, in which case the player must not
    // render a CTA — a dead button is worse than none.
    sales_url: res.data?.sales_url || "",
    can_view,
    // Drives the locked banner + its copy ("Reach Level N to unlock this course.").
    lock_reason: level_locked ? "level" : "",
    unlock_level,
    unlock_level_name,
    unlock_points,
    member_level,
    level_locked,
    modules,
  };
}

/** api/markecomplete | api/delete-markecomplete */
export async function markLessonComplete({ funnelId, memberId, jwt, courseToken, lessonToken, completed }) {
  const path = completed ? "markecomplete" : "delete-markecomplete";
  const res = await axios.post(
    `${config.API_URL}/${path}`,
    {
      funnel_id: funnelId,
      member_id: memberId,
      funnel_page_id: parseInt(courseToken, 10),
      lesson_id: parseInt(lessonToken, 10),
    },
    auth(jwt)
  );
  const d = res.data || {};
  // The bridge gates completion on quiz-pass / goal-done and returns
  // { success:false, requires_quiz|requires_goal, message } when not met.
  return {
    error: 0,
    ok: d.success !== false,
    requires_quiz: !!d.requires_quiz,
    requires_goal: !!d.requires_goal,
    message: d.message || "",
    // Gamification points this completion earned. Only the FIRST completion of a
    // lesson pays out (the member_points ledger dedups), so re-completing one
    // returns 0 and the caller shows no points toast.
    points_awarded: Number(d.points_awarded || 0),
  };
}

/**
 * api/membership-goal-submit — submit a lesson goal (assignment). Auto-approve
 * goals also complete the lesson server-side. Returns { error, status, msg }.
 */
export async function submitLessonGoal({ funnelId, memberId, jwt, courseToken, lessonToken, text, url }) {
  const res = await axios.post(
    `${config.API_URL}/membership-goal-submit`,
    {
      funnel_id: funnelId,
      member_id: memberId,
      funnel_page_id: parseInt(courseToken, 10),
      lesson_id: parseInt(lessonToken, 10),
      text: text || "",
      url: url || "",
    },
    auth(jwt)
  );
  return res.data || { error: 1 };
}

/* ================================================================== */
/* Lesson extras: quizzes, notes, downloads, discussion, bookmarks    */
/* All are JWT-gated native endpoints; lessonToken/courseToken are the */
/* stringified lesson_id / funnel_page_id.                            */
/* ================================================================== */
const base = ({ funnelId, memberId, courseToken, lessonToken }) => ({
  funnel_id: funnelId,
  member_id: memberId,
  funnel_page_id: parseInt(courseToken, 10),
  lesson_id: parseInt(lessonToken, 10),
});

/* ---------- Quiz ---------- */
export async function fetchQuiz(ctx) {
  const res = await axios.post(`${config.API_URL}/quiz-list`, base(ctx), auth(ctx.jwt));
  return res.data || {};
}
/** answers: [{ quiz_question_id, choice_rand_id }] */
export async function submitQuiz(ctx, answers) {
  const res = await axios.post(
    `${config.API_URL}/quiz-response`,
    { ...base(ctx), answer: JSON.stringify(answers) },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function fetchQuizAnswers(ctx) {
  const res = await axios.post(
    `${config.API_URL}/quiz-member-answer`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId, lesson_id: parseInt(ctx.lessonToken, 10) },
    auth(ctx.jwt)
  );
  return res.data || {};
}

/* ---------- Mock Test (standalone timed exam) ---------- */
/*
 * Distinct from the lesson Quiz above: a mock test is project-scoped (not tied
 * to a lesson) and timed. The bridge owns the clock — start stamps expires_at,
 * every save/submit re-checks it. ctx = { funnelId, memberId, jwt }. See the
 * bridge MockTestController.
 */
export async function fetchMockTests(ctx) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-list`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function fetchMockTestDetails(ctx, mockTestId) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-details`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId, mock_test_id: mockTestId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function startMockTest(ctx, mockTestId) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-start`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId, mock_test_id: mockTestId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function resumeMockTest(ctx, attemptId) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-resume`,
    { member_id: ctx.memberId, attempt_id: attemptId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function saveMockTestAnswer(ctx, { attemptId, bankQuestionId, choiceRandId, selectedValues, markedForReview }) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-save-answer`,
    {
      member_id: ctx.memberId,
      attempt_id: attemptId,
      bank_question_id: bankQuestionId,
      choice_rand_id: choiceRandId ?? "",
      // Array answer: [rand] for single/true-false, many for multi-select, [value] for numerical.
      selected_values: Array.isArray(selectedValues) ? selectedValues : undefined,
      marked_for_review: markedForReview ? 1 : 0,
    },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function submitMockTest(ctx, attemptId) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-submit`,
    { member_id: ctx.memberId, attempt_id: attemptId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
/**
 * Auto-submit the attempt when the member LEAVES the test (closes the tab,
 * refreshes, or navigates away). Uses fetch `keepalive` so the request still
 * goes out during page unload, and carries the JWT so it stays behind the same
 * auth as every other endpoint. Fire-and-forget.
 */
export function abandonMockTest(ctx, attemptId) {
  try {
    return fetch(`${config.API_URL}/mock-test-abandon`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${ctx.jwt}` },
      body: JSON.stringify({ member_id: ctx.memberId, attempt_id: attemptId }),
      keepalive: true,
    });
  } catch (e) {
    return null;
  }
}
export async function fetchMockTestResult(ctx, attemptId) {
  const res = await axios.post(
    `${config.API_URL}/mock-test-result`,
    { member_id: ctx.memberId, attempt_id: attemptId },
    auth(ctx.jwt)
  );
  return res.data || {};
}

/* ---------- Notes ---------- */
export async function fetchLessonNotes(ctx) {
  const res = await axios.post(`${config.API_URL}/get-lesson-notes`, base(ctx), auth(ctx.jwt));
  const list = res.data?.notes_list;
  return Array.isArray(list) ? list : [];
}
export async function saveLessonNote(ctx, { note, time = "", noteId } = {}) {
  const body = { ...base(ctx), notes: JSON.stringify({ time, note }) };
  if (noteId) body.lesson_note_id = noteId;
  const res = await axios.post(`${config.API_URL}/save-lesson-notes`, body, auth(ctx.jwt));
  return res.data || {};
}
export async function deleteLessonNote(ctx, noteId) {
  const res = await axios.post(
    `${config.API_URL}/delete-lesson-notes`,
    { ...base(ctx), lesson_note_id: noteId },
    auth(ctx.jwt)
  );
  return res.data || {};
}

/* ---------- Downloads ---------- */
export async function fetchDownloads(ctx) {
  const res = await axios.post(`${config.API_URL}/downloadcontent`, base(ctx), auth(ctx.jwt));
  const list = res.data?.downloadcontent;
  return Array.isArray(list) ? list : [];
}

/* ---------- Discussion (posts + comments + likes) ---------- */
export async function fetchLessonDiscussion(ctx, page = 1) {
  const res = await axios.post(
    `${config.API_URL}/getlesson-post-comment`,
    { ...base(ctx), page },
    auth(ctx.jwt)
  );
  return { posts: Array.isArray(res.data?.post) ? res.data.post : [], total: Number(res.data?.total_post || 0) };
}
export async function createLessonPost(ctx, { title, postId, image } = {}) {
  const body = { ...base(ctx), post_title: title, post_image: image || "" };
  if (postId) body.post_id = postId;
  const res = await axios.post(`${config.API_URL}/lesson-post`, body, auth(ctx.jwt));
  return res.data || {};
}
export async function createLessonComment(ctx, { postId, parentId = 0, comment, commentId, image } = {}) {
  // Spread base(ctx) so funnel_page_id + lesson_id are sent — the comments table
  // has a NOT-NULL FK on funnel_page_id, so omitting it fails the insert (matches
  // how createLessonPost builds its body).
  const body = { ...base(ctx), post_id: postId, parent_id: parentId, comment, comment_image: image || "" };
  if (commentId) body.comment_id = commentId;
  const res = await axios.post(`${config.API_URL}/lesson-comment`, body, auth(ctx.jwt));
  return res.data || {};
}
/** api/lesson-mention-members -> [{ id, name, img }] who can be @mentioned in this course's Discussion. */
export async function fetchLessonMentionMembers(ctx) {
  const res = await axios.post(`${config.API_URL}/lesson-mention-members`, base(ctx), auth(ctx.jwt));
  return Array.isArray(res.data?.members) ? res.data.members : [];
}
/**
 * api/delete-post-comment — delete a lesson discussion post (and its comments) or
 * a comment (and its replies). The server only deletes rows authored by this
 * member, so a non-author call is a no-op. type: "post" | "comment".
 */
export async function deleteLessonDiscussion(ctx, { id, type }) {
  const res = await axios.post(
    `${config.API_URL}/delete-post-comment`,
    { ...base(ctx), id, type },
    auth(ctx.jwt)
  );
  return res.data || {};
}
/** type: 1 = post, 2 = comment. fkId = post_id | comment_id. like: 1|0 */
export async function toggleLessonLike(ctx, { type, fkId, like }) {
  const res = await axios.post(
    `${config.API_URL}/lesson-like`,
    { ...base(ctx), type, fk_id: fkId, like },
    auth(ctx.jwt)
  );
  return res.data || {};
}

/* ---------- Saved / bookmarked lessons ---------- */
export async function toggleSavedLesson(ctx) {
  const res = await axios.post(
    `${config.API_URL}/toggle-saved-lesson`,
    { member_id: ctx.memberId, funnel_id: ctx.funnelId, funnel_page_id: parseInt(ctx.courseToken, 10), lesson_id: parseInt(ctx.lessonToken, 10) },
    auth(ctx.jwt)
  );
  return res.data || {};
}
export async function fetchSavedLessons({ funnelId, memberId, jwt }) {
  const res = await axios.post(
    `${config.API_URL}/get-saved-lessons`,
    { member_id: memberId, funnel_id: funnelId },
    auth(jwt)
  );
  return { lessons: Array.isArray(res.data?.lessons) ? res.data.lessons : [], count: Number(res.data?.count || 0) };
}

/* ---------- Course completion % (server-authoritative) ---------- */
export async function fetchCompletion(ctx) {
  const res = await axios.post(
    `${config.API_URL}/completeperc`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId, funnel_page_id: parseInt(ctx.courseToken, 10) },
    auth(ctx.jwt)
  );
  const d = res.data || {};
  return {
    total: Number(d.total_lesson || 0),
    completed: Number(d.completedcount || 0),
    percentage: Number(d.percentage || 0),
    certificate_url: d.certificate_url || "",
  };
}

/* ---------- Video resume positions ---------- */
/** Returns { [lesson_id]: seconds }. */
export async function fetchVideoPositions({ funnelId, memberId, jwt }) {
  const res = await axios.post(
    `${config.API_URL}/get-video-positions`,
    { member_id: memberId, funnel_id: funnelId },
    auth(jwt)
  );
  const p = res.data?.positions;
  return p && typeof p === "object" ? p : {};
}
export async function saveVideoPosition(ctx, { positionSeconds, durationSeconds }) {
  await axios.post(
    `${config.API_URL}/save-video-position`,
    {
      member_id: ctx.memberId,
      funnel_id: ctx.funnelId,
      funnel_page_id: parseInt(ctx.courseToken, 10),
      lesson_id: parseInt(ctx.lessonToken, 10),
      position_seconds: Math.round(positionSeconds || 0),
      duration_seconds: durationSeconds ? Math.round(durationSeconds) : null,
    },
    auth(ctx.jwt)
  );
  return { success: true };
}

/* ---------- Gamification (points · level · streak · badges · rewards) ---------- */
/** Funnel-wide: total points + level ladder + progress to next. */
export async function fetchMemberLevel(ctx) {
  const res = await axios.post(
    `${config.API_URL}/get-member-level`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
/** Funnel-wide: current + longest streak. */
export async function fetchMemberStreak(ctx) {
  const res = await axios.post(
    `${config.API_URL}/get-member-streak`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
/** Course-scoped: badges with earned (show=1) / locked (need_points). */
export async function fetchMemberBadges(ctx) {
  const res = await axios.post(`${config.API_URL}/member-badge-list`, base(ctx), auth(ctx.jwt));
  return { badges: Array.isArray(res.data?.badges) ? res.data.badges : [], countText: res.data?.badges_count_text || "" };
}
/** Course-scoped: the next badge to unlock. */
export async function fetchNextBadge(ctx) {
  const res = await axios.post(`${config.API_URL}/get-next-badge`, base(ctx), auth(ctx.jwt));
  const b = res.data?.badges;
  return { badge: b && b.badge_id ? b : null, receivedText: res.data?.badge_members_received_text || res.data?.badge_members_receved_text || "" };
}
/** Course-scoped: rewards with earned / locked + redemption. */
export async function fetchMemberRewards(ctx) {
  const res = await axios.post(`${config.API_URL}/member-reward-list`, base(ctx), auth(ctx.jwt));
  return { rewards: Array.isArray(res.data?.rewards) ? res.data.rewards : [], totalPoints: Number(res.data?.tot_points || 0) };
}
/** Spend points to redeem a reward. Returns { success, total_points, points_spent, message }. */
export async function redeemReward(ctx, rewardId) {
  const res = await axios.post(
    `${config.API_URL}/redeem-reward`,
    { funnel_id: ctx.funnelId, member_id: ctx.memberId, reward_id: rewardId },
    auth(ctx.jwt)
  );
  return res.data || {};
}
/** Course-scoped: the point-award config ({ lessoncomplete, quiz_pass, ... }). */
export async function fetchGamifyPoints(ctx) {
  const res = await axios.post(`${config.API_URL}/get-gamify-points`, base(ctx), auth(ctx.jwt));
  try { return JSON.parse(res.data?.gamifypoint || "{}") || {}; } catch { return {}; }
}
